import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { name: "", product_name: "", product_id: "", min_quantity: 1, max_quantity: 0, status: "Active" };

export default function ProductLimits() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get("/product-limits");
    setRows(r.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/product-limits", form);
      toast.success("Product limit created");
      setOpen(false);
      setForm(empty);
      load();
    } catch { toast.error("Failed"); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this product limit?")) return;
    await api.delete(`/product-limits/${id}`);
    load();
  }

  async function toggle(row) {
    const status = row.status === "Active" ? "Draft" : "Active";
    await api.patch(`/product-limits/${row.id}`, { status });
    load();
  }

  return (
    <div data-testid="product-limits-page">
      <PageHeader
        title="Product Limits"
        subtitle="Set purchase quantity limits at the product level"
        actions={
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="product-limit-create-btn">
            <Plus size={14} /> Create product limit
          </button>
        }
      />

      <div className="b2b-card">
        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--brand-muted)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No product limits" subtitle="Enforce per-product min/max quantities." testId="product-limits-empty" />
        ) : (
          <table className="b2b-table" data-testid="product-limits-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Product</th>
                <th>Min / Max</th>
                <th>Status</th>
                <th>Created at</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`product-limit-row-${r.id}`}>
                  <td className="font-medium">{r.name}</td>
                  <td>{r.product_name || <span className="text-gray-400">—</span>}</td>
                  <td>{r.min_quantity} — {r.max_quantity || "∞"}</td>
                  <td>
                    <label className="switch">
                      <input type="checkbox" checked={r.status === "Active"} onChange={() => toggle(r)} data-testid={`product-limit-toggle-${r.id}`} />
                      <span className="slider" />
                    </label>
                    <span className={`ml-2 badge ${r.status === "Active" ? "badge-active" : "badge-draft"}`}>{r.status}</span>
                  </td>
                  <td className="text-sm" style={{ color: "var(--brand-muted)" }}>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="btn-danger" onClick={() => remove(r.id)} data-testid={`product-limit-delete-${r.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create product limit"
        testId="product-limit-modal"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)} data-testid="product-limit-cancel">Cancel</button>
            <button className="btn-primary" onClick={create} data-testid="product-limit-save">Create limit</button>
          </>
        }
      >
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="form-label">Rule name</label>
            <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-limit-name" />
          </div>
          <div>
            <label className="form-label">Product name</label>
            <input className="form-input" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="product-limit-product-name" />
          </div>
          <div>
            <label className="form-label">Shopify product ID (optional)</label>
            <input className="form-input" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="product-limit-product-id" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Min quantity</label>
              <input type="number" className="form-input" value={form.min_quantity}
                onChange={(e) => setForm({ ...form, min_quantity: Number(e.target.value) })} data-testid="product-limit-min" />
            </div>
            <div>
              <label className="form-label">Max quantity (0 for unlimited)</label>
              <input type="number" className="form-input" value={form.max_quantity}
                onChange={(e) => setForm({ ...form, max_quantity: Number(e.target.value) })} data-testid="product-limit-max" />
            </div>
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="product-limit-status">
              <option>Active</option>
              <option>Draft</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
